import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckIcon, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";

import { useSyncFeedback, SyncFeedbackInput } from "@/feedback/api";
import { useUser } from "@/api/api";

// Validation schema with conditional requirements
const syncFeedbackSchema = z.object({
  message: z.string().min(1, "Message is required"),
  name: z.string().optional(),
  phone_number: z.string().optional(),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
});

type SyncFeedbackFormData = z.infer<typeof syncFeedbackSchema>;

interface SyncFeedbackFormProps {
  sensorKitSerialNumber: string;
}

export function SyncFeedbackForm({ sensorKitSerialNumber }: SyncFeedbackFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { data: user } = useUser();
  const isAuthenticated = !!user;

  const syncFeedbackMutation = useSyncFeedback();

  const form = useForm<SyncFeedbackFormData>({
    resolver: zodResolver(syncFeedbackSchema),
    defaultValues: {
      message: "",
      name: "",
      phone_number: "",
      email: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: SyncFeedbackFormData) => {
    // Client-side validation for unauthenticated users
    if (!isAuthenticated) {
      if (!data.name || data.name.trim() === "") {
        form.setError("name", {
          type: "manual",
          message: "Name is required when not logged in",
        });
        return;
      }
      if (!data.phone_number && !data.email) {
        form.setError("phone_number", {
          type: "manual",
          message: "Either phone number or email is required",
        });
        form.setError("email", {
          type: "manual",
          message: "Either phone number or email is required",
        });
        return;
      }
    }

    const payload: SyncFeedbackInput = {
      sensor_kit_serial_number: sensorKitSerialNumber,
      message: data.message,
      ...(data.name && { name: data.name }),
      ...(data.phone_number && { phone_number: data.phone_number }),
      ...(data.email && { email: data.email }),
    };

    try {
      await syncFeedbackMutation.mutateAsync(payload);

      // Show success message
      setIsSubmitted(true);
    } catch (error: any) {
      // Handle server validation errors
      if (error?.response?.data) {
        const serverErrors = error.response.data;

        // Set field-specific errors
        Object.keys(serverErrors).forEach((field) => {
          if (field in form.getValues()) {
            form.setError(field as any, {
              type: "server",
              message: serverErrors[field][0],
            });
          }
        });
      } else {
        toast.error("Failed to send feedback", {
          description: "Please try again.",
        });
      }
    }
  };

  const handleSubmitAnother = () => {
    setIsSubmitted(false);
    form.reset();
  };

  return (
    <CollapsibleCard
        title={
          <div>
            <div className="text-lg font-semibold">Something not quite right?</div>
            <p className="text-sm text-muted-foreground font-normal">
              Enter your details here and we'll be in touch shortly
            </p>
          </div>
        }
        defaultOpen={false}
        onOpenChange={setIsOpen}
      >
        {isSubmitted ? (
          <div className="text-center space-y-4">
            <div className="flex flex-col items-center p-4 gap-2">
              <div className="rounded-full bg-green-100 p-3">
                <CheckIcon className="text-green-700"/>
              </div>
              <h3 className="text-lg font-semibold">Message sent!</h3>
              <p className="text-sm text-muted-foreground">
                We'll be in touch as soon as possible.
              </p>
            </div>
            <Button onClick={handleSubmitAnother} variant="outline" className="w-full">
              Submit another
            </Button>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Message field - always required */}
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Message <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us what's not working..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Show contact fields only for unauthenticated users */}
              {!isAuthenticated && (
                <>
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Name <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Your name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="your@email.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="(555) 123-4567"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <p className="text-sm text-muted-foreground">
                    * Either email or phone number is required
                  </p>
                </>
              )}

              <Button
                type="submit"
                variant="thalo"
                disabled={syncFeedbackMutation.isPending}
                className="w-full"
              >
                {syncFeedbackMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {syncFeedbackMutation.isPending ? "Sending..." : "Send Feedback"}
              </Button>
            </form>
          </Form>
        )}
      </CollapsibleCard>
  );
}
